import { Alert, AlertDescription, AlertTitle, Box, Button, FormControl, HStack, Select, Stack, Switch, VStack } from '@chakra-ui/react'
import type { IInsuranceParams } from 'BrokerTsLib/src/dtos'
import { Insurer } from 'BrokerTsLib/src/enums'
import { useFormContext } from 'BrokerTsLib/src/rhf'
import { useContext, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { FormLabel } from '../../components/FormLabel'
import { InputGroup } from '../../components/InputGroup'
import { useApp } from '../../hooks/useApp'
import { useCloneInsuranceParams, useFindContractNumber } from '../../hooks/useInsuranceParams'
import { Check, Cloud } from '../../icons'
import { insuranceParamsContext } from '../../layouts/Wizard'

export const ContractReplacement = () => {
  const { app } = useApp()
  const { insuranceParams } = useContext(insuranceParamsContext)
  const isContracted = !!insuranceParams?.generalParams.contractNumber
  const navigate = useNavigate()
  const { register, watch, getValues, setValue, formState } = useFormContext<IInsuranceParams>()

  const [showResult, setShowResult] = useState(false)

  const contractNumber = watch('generalParams.contractReplacementNumber')
  const insurer = watch('generalParams.contractReplacementInsurerId')
  const { refetch, isLoading, isError, isFetched, data } = useFindContractNumber(
    contractNumber ?? '',
    insurer ?? 0,
    {
      enabled: false,
    },
  )

  const replacement = contractNumber !== undefined && contractNumber !== null
  const foundParamsId = data?.data.insuranceParamsId
  const errors = formState.errors.generalParams?.contractReplacementNumber
  const insurerErrors = formState.errors.generalParams?.contractReplacementInsurerId

  const { mutateAsync: cloneInsuranceParams, isLoading: isLoadingClone } = useCloneInsuranceParams(
    foundParamsId?.toString(),
    true,
  )
  const onCloneClick = () => {
    cloneInsuranceParams()
      .then(({ data: { insuranceParamsId } }) => {
        navigate(`/${app}/wizard/1/${insuranceParamsId}`)
      })
      .catch((data) => {
        console.log('an error occurred when cloning insurance params', data)
        alert('Při klonování došlo k chybě')
      })
  }

  let tooltipText =
    'Pouze pro pojišťovny Allianz, ČPP a Kooperativa. Náhradou je sjednání nové pojistné smlouvy, která nahrazuje a ruší pojistnou smlouvu předešlou. Konec pojištění nahrazované smlouvy je shodný s počátkem pojištění navazující smlouvy.'
  if (app == 'chaloupka') {
    tooltipText =
      'Pouze pro pojišťovny Allianz, ČPP a ČSOB. Náhradou je sjednání nové pojistné smlouvy, která nahrazuje a ruší pojistnou smlouvu předešlou. Konec pojištění nahrazované smlouvy je shodný s počátkem pojištění navazující smlouvy.'
  }

  return (
    <>
      <FormControl>
        <FormControl>
          <FormLabel tooltip={tooltipText}>Náhrada smlouvy</FormLabel>
        </FormControl>

        <FormControl sx={{ display: 'flex', alignItems: 'center', alignSelf: 'end', py: 1 }}>
          <FormLabel htmlFor="contractReplacementSwitch" variant="switch">
            Ne
          </FormLabel>
          <Switch
            id="contractReplacementSwitch"
            isChecked={replacement}
            isDisabled={isContracted}
            onChange={(e) => {
              if (!e.target.checked) {
                setValue('generalParams.contractReplacementNumber', undefined)
                setValue('generalParams.contractReplacementInsurerId', undefined)
              } else {
                setValue('generalParams.contractReplacementNumber', '')
                setValue('generalParams.contractReplacementInsurerId', undefined)
              }
            }}
          />
          <FormLabel htmlFor="contractReplacementSwitch" variant="switch">
            Ano
          </FormLabel>
        </FormControl>
      </FormControl>

      {replacement && (
        <VStack alignItems="start">
          <HStack alignItems="start">
            <FormControl isInvalid={!!errors} isRequired>
              <FormLabel
                htmlFor="contractReplacementNumber"
                tooltip="V případě, že je nahrazovaná smlouva sjednaná v CARkulce je možné si načíst údaje z původní smlouvy. V případě, že smlouva není sjednaná v CARkulce je nutné zadat údaje ručně. Na sjednání náhrady nemá tato funkce vliv."
              >
                Číslo smlouvy
              </FormLabel>

              <InputGroup
                id="contractReplacementNumber"
                placeholder="Číslo smlouvy"
                isDisabled={isContracted}
                htmlSize={16}
                errors={errors}
                {...register('generalParams.contractReplacementNumber')}
              />
            </FormControl>

            <FormControl isInvalid={!!insurerErrors}>
              <FormLabel>&nbsp;</FormLabel>
              <Box sx={{ display: 'grid', h: '100%', alignItems: 'end', alignSelf: 'end' }}>
                <Select
                  id="generalParams.contractReplacementInsurerId"
                  isDisabled={isContracted}
                  defaultValue={
                    getValues('generalParams.contractReplacementInsurerId')?.toString() ?? ''
                  }
                  onChange={(e) => {
                    setValue('generalParams.contractReplacementInsurerId', parseInt(e.target.value))
                  }}
                >
                  <option value="" disabled>
                    Vyberte
                  </option>
                  <option value={Insurer.CPP.toString()}>ČPP</option>
                  <option value={Insurer.Allianz.toString()}>Allianz</option>
                  {app != 'chaloupka' && (
                    <option value={Insurer.Kooperativa.toString()}>Kooperativa</option>
                  )}
                  {app == 'chaloupka' && (
                    <option value={Insurer.Csob.toString()}>ČSOB Pojišťovna</option>
                  )}
                </Select>
              </Box>

              {/* {!!insurerErrors && <FormErrorMessage>{insurerErrors.message}</FormErrorMessage>} */}
            </FormControl>

            {!isContracted && (
              <Box sx={{ display: 'grid', h: '100%', alignItems: 'start', pt: 6 }}>
                <Button
                  type="button"
                  variant="outline"
                  isLoading={isLoading}
                  isDisabled={isLoading || !!errors || !!insurerErrors}
                  onClick={() => {
                    refetch().finally(() => {
                      setShowResult(true)
                    })
                  }}
                >
                  <Cloud color="secondary.500" />
                  Načíst údaje ze smlouvy
                </Button>
              </Box>
            )}
          </HStack>

          {!isLoading && showResult && (
            <>
              {!!foundParamsId && (
                <Alert status='success' as={Stack}>
                  <AlertTitle>Smlouva byla nalezena. </AlertTitle>
                  <AlertDescription>Přejete si vyplnit data podle původní smlouvy?</AlertDescription>
                  <Button
                    variant="outline"
                    isLoading={isLoadingClone}
                    onClick={onCloneClick}
                  >
                    <HStack>
                      <Check />
                      <span>Vyplnit data</span>
                    </HStack>
                  </Button>
                </Alert>
              )}

              {(!foundParamsId || isError) && isFetched && (
                <Alert status='error' as={Stack}>
                  <AlertTitle>Smlouva nebyla nalezena.</AlertTitle>
                  <AlertDescription>Načíst data z původní smlouvy není možné, zadejte údaje ručně.</AlertDescription>
                </Alert>
              )}
            </>
          )}
        </VStack>
      )}
    </>
  )
}
